import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    NgZone,
    output,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    LoadMoreComponent,
    ScrollTrackerDirective,
    ScrollTrackerEvent,
} from '@interacta-shared/ui';
import { getNextPageToken, PageTokenInfo } from '@interacta-shared/util';
import { focusFirstNewChild } from '@interacta-shared/util-common';
import { AIConvoHistoryListItemComponent } from '@modules/ai-convo/components/ai-convo-history-item/ai-convo-history-list-item.component';
import { groupHistoryByDate } from '@modules/ai-convo/helpers/ai-convo-history.utils';
import {
    AIConvoHistoryGroup,
    AIConvoHistoryItem,
} from '@modules/ai-convo/models/ai-convo-history.model';
import { AIConvoActions } from '@modules/ai-convo/store/ai-convo.actions';
import { selectAIConvoHistory } from '@modules/ai-convo/store/ai-convo.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

const GROUP_LABEL_KEYS: Record<AIConvoHistoryGroup, string> = {
    today: 'AI.SIDE_PANEL.HISTORY.TODAY',
    yesterday: 'AI.SIDE_PANEL.HISTORY.YESTERDAY',
    last7days: 'AI.SIDE_PANEL.HISTORY.LAST_7_DAYS',
    older: 'AI.SIDE_PANEL.HISTORY.OLDER',
    ungrouped: '',
};

@Component({
    selector: 'interacta-ai-convo-history-panel',
    templateUrl: './ai-convo-history-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AIConvoHistoryListItemComponent,
        LoadMoreComponent,
        ScrollTrackerDirective,
        TranslatePipe,
    ],
})
export class AIConvoHistoryPanelComponent {
    readonly back = output<void>();
    readonly itemClick = output<AIConvoHistoryItem>();
    readonly renameClick = output<AIConvoHistoryItem>();
    readonly deleteClick = output<AIConvoHistoryItem>();

    private readonly store = inject(Store);
    private readonly zone = inject(NgZone);

    readonly scrollContainer =
        viewChild<ElementRef<HTMLElement>>('historyList');
    private readonly loadMoreContainer =
        viewChild<ElementRef<HTMLElement>>('loadMoreContainer');

    readonly history = this.store.selectSignal(selectAIConvoHistory);

    readonly groupedHistory = computed(() =>
        groupHistoryByDate(this.history().list),
    );

    readonly groupLabelKeys = GROUP_LABEL_KEYS;

    readonly canAutoLoad = computed(() => {
        const history = this.history();
        return (
            !history.isFetching &&
            history.nextPageToken.tag === 'regularLoading'
        );
    });

    private pendingObserver: MutationObserver | null = null;
    private autoLoadScheduled = false;

    constructor() {
        // Auto-fill the viewport while the list still fits (the "Load more"
        // button is visible without scrolling), mirroring post-table-v2.
        // Observe only the tiny load-more container, outside Angular, so nothing
        // runs while the user reads: it mutates just when the button appears
        // (childList) or swaps button↔loader on a fetch (class) — never on
        // list/message changes. Each mutation re-checks; overflow (or no page
        // left) stops it.
        const observer = new MutationObserver(() => this.scheduleAutoLoad());
        toObservable(this.loadMoreContainer)
            .pipe(takeUntilDestroyed())
            .subscribe((container) =>
                this.zone.runOutsideAngular(() => {
                    observer.disconnect();
                    if (container) {
                        observer.observe(container.nativeElement, {
                            attributeFilter: ['class'],
                            attributes: true,
                            childList: true,
                            subtree: true,
                        });
                        this.scheduleAutoLoad();
                    }
                }),
            );
        inject(DestroyRef).onDestroy(() => observer.disconnect());
    }

    onScroll(event: ScrollTrackerEvent): void {
        const history = this.history();
        if (
            event.offsetReached &&
            !history.isFetching &&
            history.nextPageToken.tag === 'regularLoading'
        ) {
            this.loadMore(history.nextPageToken);
        }
    }

    loadMore(pageTokenInfo: PageTokenInfo, container?: HTMLElement): void {
        if (container) {
            this.pendingObserver = focusFirstNewChild({
                container,
                currentChildCount: this.history().list.length,
                pendingObserver: this.pendingObserver,
                childSelector: 'interacta-ai-convo-history-list-item',
            });
        }
        const pageToken = getNextPageToken(pageTokenInfo) ?? undefined;
        this.store.dispatch(AIConvoActions.fetchHistory({ pageToken }));
    }

    private scheduleAutoLoad(): void {
        if (this.autoLoadScheduled) return;
        this.autoLoadScheduled = true;
        requestAnimationFrame(() => {
            this.autoLoadScheduled = false;
            this.checkAutoLoad();
        });
    }

    private checkAutoLoad(): void {
        if (!this.canAutoLoad()) return;

        const el = this.scrollContainer()?.nativeElement;
        if (!el || el.scrollHeight - el.clientHeight > 1) return;

        // Auto-load fills the viewport without moving focus; keyboard users rely
        // on the visible "Load more" button (which handles focus via `loadMore`).
        const pageToken =
            getNextPageToken(this.history().nextPageToken) ?? undefined;
        this.zone.run(() =>
            this.store.dispatch(AIConvoActions.fetchHistory({ pageToken })),
        );
    }
}
